import Vue from 'vue'
import Router from 'vue-router'

import store from '../store' // your vuex store

const ifNotAuthenticated = (to, from, next) => {
  if (!store.getters.isAuthenticated) {
    next()
    return
  }
  next('/')
}

const ifAuthenticated = (to, from, next) => {
  if (store.getters.isAuthenticated) {
    next()
    return
  }
  next('/login')
}

// Containers
const DefaultContainer = () => import('@/containers/DefaultContainer')

// Views
const Dashboard = () => import('@/views/Dashboard')

// Admins
const Admins = () => import('@/views/admins/Admins')
const AddAdmin = () => import('@/views/admins/Add')
const EditAdmin = () => import('@/views/admins/Edit')

// Companies
const Companies = () => import('@/views/companies/Companies')
const RegisterCompany = () => import('@/views/companies/Register')
const EditCompany = () => import('@/views/companies/Edit')
const Company = () => import('@/views/companies/Company')

// Devices
const Devices = () => import('@/views/devices/Devices')
const RegisterDevice = () => import('@/views/devices/Register')
const EditDevice = () => import('@/views/devices/Edit')
const Device = () => import('@/views/devices/Device')

// Organizations
const Organizations = () => import('@/views/organizations/Organizations')
const CreateOrganization = () => import('@/views/organizations/Create')
const EditOrganization = () => import('@/views/organizations/Edit')

// Pages
const ActiveUsers = () => import('@/views/pages/ActiveUsers')
const DeviceUsage = () => import('@/views/pages/DeviceUsage')
const Page404 = () => import('@/views/pages/Page404')
const Page500 = () => import('@/views/pages/Page500')
const Login = () => import('@/views/pages/Login')
const LevelAccess = () => import('@/views/pages/LevelAccess')
const ForgotPassword = () => import('@/views/pages/ForgotPassword')
const ResetPassword = () => import('@/views/pages/ResetPassword')

// Promotions
const Promotions = () => import('@/views/promotions/Promotions')
const CreatePromotion = () => import('@/views/promotions/Create')
const EditPromotion = () => import('@/views/promotions/Edit')

// Settings
const Settings = () => import('@/views/pages/Settings')
const Account = () => import('@/views/settings/Account')
const Password = () => import('@/views/settings/Password')
const Email = () => import('@/views/settings/Email')

// Users
const User = () => import('@/views/users/User')

Vue.use(Router)

export default new Router({
  mode: 'hash', // https://router.vuejs.org/api/#mode
  linkActiveClass: 'open active',
  scrollBehavior: () => ({ y: 0 }),
  routes: [
    {
      path: '/',
      redirect: '/dashboard',
      name: 'Home',
      component: DefaultContainer,
      beforeEnter: ifAuthenticated,
      children: [
        {
          path: 'dashboard',
          name: 'Dashboard',
          component: Dashboard
        },
        {
          path: 'settings',
          component: Settings,
          redirect: '/settings/account',
          children: [
            {
              path: 'account',
              component: Account
            },
            {
              path: 'password',
              component: Password
            },
            {
              path: 'email',
              component: Email
            }
          ]
        },
        {
          path: '/companies',
          name: 'Companies',
          redirect: '/companies/list',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'list',
              name: 'Companies List',
              component: Companies
            },
            {
              path: 'register',
              name: 'Register Company',
              component: RegisterCompany
            },
            {
              path: 'edit/:id',
              name: 'Edit Company',
              component: EditCompany
            },
            {
              path: ':id',
              name: 'Company',
              component: Company
            }
          ]
        },
        {
          path: 'devices',
          name: 'Devices',
          redirect: '/devices/list',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'list',
              name: 'Device List',
              component: Devices
            },
            {
              path: 'register',
              name: 'Register Device',
              component: RegisterDevice
            },
            {
              path: 'edit/:id',
              name: 'Edit Device',
              component: EditDevice
            },
            {
              path: ':id',
              name: 'Device',
              component: Device
            }
          ]
        },
        {
          path: 'promotions',
          name: 'Promotions',
          redirect: '/promotions/list',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'list',
              name: 'Promotion List',
              component: Promotions
            },
            {
              path: 'create',
              name: 'Create Promotion',
              component: CreatePromotion
            },
            {
              path: 'edit/:id',
              name: 'Edit Promotion',
              component: EditPromotion
            }
          ]
        },
        {
          path: '/admins',
          name: 'Admins',
          redirect: '/admins/list',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'list',
              name: 'Admins List',
              component: Admins
            },
            {
              path: 'add',
              name: 'Add Admin',
              component: AddAdmin
            },
            {
              path: 'edit/:id',
              name: 'Edit Admin',
              component: EditAdmin
            }
          ]
        },
        {
          path: '/organizations',
          name: 'Organizations',
          redirect: '/organizations/list',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: 'list',
              name: 'Organizations List',
              component: Organizations
            },
            {
              path: 'create',
              name: 'Create Organization',
              component: CreateOrganization
            },
            {
              path: 'edit/:id',
              name: 'Edit Organization',
              component: EditOrganization
            }
          ]
        },
        {
          path: '/users',
          name: 'Users',
          component: {
            render (c) { return c('router-view') }
          },
          children: [
            {
              path: ':id',
              name: 'User',
              component: User
            }
          ]
        },
        {
          path: 'active-users',
          name: 'Active Users',
          component: ActiveUsers
        },
        {
          path: 'device-usage',
          name: 'Device Usage',
          component: DeviceUsage
        },
        {
          path: 'level-access',
          name: 'Level Access',
          component: LevelAccess
        }
      ]
    },
    {
      path: '/',
      redirect: '/pages/404',
      name: 'Pages',
      beforeEnter: ifNotAuthenticated,
      component: {
        render (c) { return c('router-view') }
      },
      children: [
        {
          path: '404',
          name: 'Page404',
          component: Page404
        },
        {
          path: '500',
          name: 'Page500',
          component: Page500
        },
        {
          path: 'login',
          name: 'Login',
          component: Login
        },
        {
          path: 'forgot-password',
          name: 'Forgot Password',
          component: ForgotPassword
        },
        {
          path: 'reset-password',
          name: 'Reset Password',
          component: ResetPassword
        }
      ]
    }
  ]
})
